import { useEffect, useRef, useState } from 'react'
import api from '../lib/api'
import { toastError } from '../lib/toast'

type WompiConfig = {
  public_key: string
  reference: string
  amount_in_cents: number
  currency: string
  integrity_signature: string
  redirect_url: string
}

/**
 * Shared Wompi checkout flow: posts a payment-intent to the API, then injects
 * the Wompi widget script and auto-clicks its button to open the checkout.
 * Used by both course and program-level purchases so a fix here covers both.
 */
export function useWompiCheckout(onWidgetSettled: () => void) {
  const [wompiConfig, setWompiConfig] = useState<WompiConfig | null>(null)
  const onWidgetSettledRef = useRef(onWidgetSettled)
  useEffect(() => {
    onWidgetSettledRef.current = onWidgetSettled
  }, [onWidgetSettled])

  useEffect(() => {
    if (!wompiConfig) return
    const containerId = 'wompi-checkout-container'
    let container = document.getElementById(containerId)
    if (!container) {
      container = document.createElement('div')
      container.id = containerId
      container.style.display = 'none'
      document.body.appendChild(container)
    }
    container.innerHTML = ''

    const script = document.createElement('script')
    script.src = 'https://checkout.wompi.co/widget.js'
    script.setAttribute('data-render', 'button')
    script.setAttribute('data-public-key', wompiConfig.public_key)
    script.setAttribute('data-currency', wompiConfig.currency)
    script.setAttribute('data-amount-in-cents', String(wompiConfig.amount_in_cents))
    script.setAttribute('data-reference', wompiConfig.reference)
    script.setAttribute('data-signature:integrity', wompiConfig.integrity_signature)
    script.setAttribute('data-redirect-url', wompiConfig.redirect_url)
    script.onload = () => {
      const btn = container!.querySelector('button')
      if (btn) btn.click()
      onWidgetSettledRef.current()
    }
    script.onerror = () => {
      toastError('No fue posible cargar la pasarela de pago. Intenta de nuevo.')
      onWidgetSettledRef.current()
    }
    container.appendChild(script)

    return () => {
      container?.remove()
    }
  }, [wompiConfig])

  /** Returns an error message on failure, or null on success. */
  const startCheckout = async (
    endpoint: string,
    body: Record<string, unknown>,
  ): Promise<string | null> => {
    try {
      const { data } = await api.post(endpoint, body)
      setWompiConfig(data.data.wompi)
      return null
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      return msg ?? 'No fue posible iniciar el pago. Intenta de nuevo.'
    }
  }

  return { startCheckout }
}
